/* LT-10 — Tender Adjudication screen.
 *
 * The bridge between LUX cost basis and Client final price. LUX-only view.
 * Sections (top-down):
 *   (a) Sub-header strip with "View change history" + "Create client estimate"
 *   (b) Commercial inputs panel (4-col grid): duration, overhead %, profit %, contingency
 *   (c) Cost breakdown table (Labour / Materials / Plant / Prov Sums / Prelims / Contingency \u2192 subtotal \u2192 final adj \u2192 client total)
 *   (d) Final adjustment panel (collapsed by default; target-price OR manual; mandatory notes; show-as-discount toggle)
 *   (e) Tender checks panel (read-only sanity ratios + £/m² benchmarks + day-rate sanity)
 *
 * State model: single payload from `GET /api/tenders/{id}/adjudication`.
 * Commercial-input edits flush to PUT with 800ms debounce. Final adjustment
 * goes through dedicated POST/DELETE endpoints with confirmation + notes
 * validation.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronDown, ChevronRight, Sparkles, History, FileCheck, Info,
  AlertTriangle, Calculator, Building2, Hammer, Construction,
  Receipt, Percent, Plus, X, Save,
} from "lucide-react";
import Banknote from "lucide-react/dist/esm/icons/banknote";
import { toast } from "sonner";
import { api } from "../lib/api";
import { gbp, num, ukDate } from "../lib/format";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { OverrideReviewPanel } from "../components/OverrideReviewPanel";
import HoursPerTradePanel from "../components/HoursPerTradePanel";
import CreateClientEstimateDialog from "../components/CreateClientEstimateDialog";

const cls = (...a) => a.filter(Boolean).join(" ");

const TRAFFIC_LIGHT = {
  green: { bg: "#E8EFE9", text: "#2F4F3E", border: "#D6E2D8", label: "On target" },
  amber: { bg: "#FEF3E2", text: "#7A4C0A", border: "#F2D9A6", label: "Outside target" },
  red:   { bg: "#FCE9E6", text: "#A1311E", border: "#F2C5BE", label: "Way off target" },
};

export default function TenderAdjudication({ tenderId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState({});
  const debounceTimer = useRef(null);
  const [adjOpen, setAdjOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);

  const load = () => {
    setLoading(true);
    api.get(`/tenders/${tenderId}/adjudication`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load Adjudication"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [tenderId]); // eslint-disable-line react-hooks/exhaustive-deps

  const a = data?.adjudication;
  const breakdown = a?.breakdown;
  const checks = a?.tender_checks;
  const tender = data?.tender;

  const hasFinalAdj = (a?.final_adjustment_value || 0) !== 0;

  // ---- Optimistic local edit + debounced PUT ----
  const updateLocal = (patch) => {
    setData((d) => ({ ...d, adjudication: { ...d.adjudication, ...patch } }));
    setPending((p) => ({ ...p, ...patch }));
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(flush, 800);
  };

  const flush = async () => {
    const body = pending;
    setPending({});
    if (!Object.keys(body).length) return;
    try {
      const r = await api.put(`/tenders/${tenderId}/adjudication`, body);
      setData(r.data);
    } catch {
      toast.error("Failed to save commercial inputs");
    }
  };

  // ---- Final adjustment apply / remove ----
  const applyAdjustment = async ({ target_client_price, manual_value, notes }) => {
    try {
      const r = await api.post(
        `/tenders/${tenderId}/adjudication/apply-adjustment`,
        { target_client_price, manual_value, notes },
      );
      setData(r.data);
      setConfirmDialog(null);
      toast.success("Final adjustment applied");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to apply adjustment";
      toast.error(typeof msg === "string" ? msg : "Failed to apply adjustment");
    }
  };

  const removeAdjustment = async () => {
    try {
      const r = await api.delete(`/tenders/${tenderId}/adjudication/adjustment`);
      setData(r.data);
      setConfirmDialog(null);
      toast.success("Adjustment removed");
    } catch {
      toast.error("Failed to remove adjustment");
    }
  };

  const [createEstimateOpen, setCreateEstimateOpen] = useState(false);
  const createClientEstimate = () => setCreateEstimateOpen(true);
  const onEstimateIssued = () => {
    // Refresh adjudication data + surface the Prepared banner
    // (loaded by the top-level LifecycleStateBanner).
    api.get(`/tenders/${tenderId}/adjudication`).then((r) => setData(r.data)).catch(() => {});
  };

  if (loading || !a) {
    return <div className="text-ink-4 py-8" style={{ fontSize: 12.5 }}>Loading…</div>;
  }

  return (
    <div className="space-y-5" data-testid="tender-adjudication">
      {/* --- Sub-header --- */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-ink" style={{ fontSize: 20, fontWeight: 600 }}>Adjudication</h2>
          <p className="text-ink-3 mt-1" style={{ fontSize: 12.5 }}>
            LUX view — bridges cost basis to client price. Client never sees this screen.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* LT-16 Part 3d — retired duplicate "View change history"
              button. The tender-header HistoryButton already renders
              the audit drawer; maintaining two entry points guarantees
              they drift. */}
          <button
            className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-md bg-accent text-white shadow-soft hover:bg-accent-deep"
            style={{ fontSize: 12.5, fontWeight: 500 }}
            onClick={createClientEstimate}
            data-testid="adjudication-create-client-estimate-btn"
          >
            <FileCheck size={14} /> Create client estimate
          </button>
        </div>
      </div>
      <CreateClientEstimateDialog
        open={createEstimateOpen}
        onOpenChange={setCreateEstimateOpen}
        tenderId={tenderId}
        tenderRef={data?.tender?.ref}
        onIssued={onEstimateIssued}
      />

      {/* LT-Estimate-UX-01 Section 5 — override review panel. Collapsed
          summary of every library line whose values diverge from their
          snapshot on this tender. Non-blocking. Keep / Update-library /
          Save-as-new decisions land here so the estimator finalises the
          audit trail before creating the client estimate. */}
      <OverrideReviewPanel
        tenderId={tenderId}
        onOpenLine={(lineId) => {
          // Nav to Estimate tab, anchor to line id. TenderDetail listens
          // for ?tab=estimate&line=<id> and highlights the row.
          const url = new URL(window.location.href);
          url.searchParams.set("tab", "estimate");
          url.searchParams.set("line", lineId);
          window.location.href = url.toString();
        }}
        onOpenLibraryUpdate={() => toast.info(
          "Open the line in the Estimate tab and use the row menu \u2192 " +
          "'Update library item with these values\u2026' to push guarded changes."
        )}
        onOpenSaveAsNew={() => toast.info(
          "Open the line in the Estimate tab and use the row menu \u2192 " +
          "'Save as new library item' to promote it into a new item."
        )}
      />

      {/* --- (b) Commercial inputs --- */}
      <div className="rounded-lg border border-line bg-surface p-4">
        <div className="col-head mb-3" style={{ fontSize: 10 }}>Commercial inputs</div>
        <div className="grid grid-cols-4 gap-4">
          <DurationInput
            calculated={a.calculated_duration_weeks}
            adjustment={a.manual_duration_adjustment_weeks}
            project={a.project_duration_weeks}
            onChange={(v) => updateLocal({ manual_duration_adjustment_weeks: v })}
          />
          <PctInput
            label="Overhead %"
            value={a.overhead_pct}
            onChange={(v) => updateLocal({ overhead_pct: v })}
            testId="adjudication-overhead-pct"
          />
          <PctInput
            label="Profit %"
            value={a.profit_pct}
            onChange={(v) => updateLocal({ profit_pct: v })}
            testId="adjudication-profit-pct"
            footer={<span className="text-ink-4" style={{ fontSize: 11 }}>Combined O&amp;P: {num(a.oap_pct, 1)}%</span>}
          />
          <ContingencyInput
            value={a.contingency_value}
            isPct={a.contingency_is_percentage}
            pct={a.contingency_pct}
            computedTotal={breakdown.contingency_total}
            onChange={updateLocal}
          />
        </div>
      </div>

      {/* --- (c) Cost breakdown --- */}
      <div className="rounded-lg border border-line bg-surface overflow-hidden">
        <div className="px-4 py-3 border-b border-line">
          <div className="flex items-center justify-between">
            <h3 className="text-ink" style={{ fontSize: 14, fontWeight: 600 }}>
              Cost breakdown — LUX to client
            </h3>
            <span className="text-ink-4" style={{ fontSize: 11 }}>
              All figures in GBP. Subby allowances flow through Pro Sums.
            </span>
          </div>
        </div>
        <table className="w-full" style={{ fontSize: 12.5 }}>
          <thead>
            <tr className="border-b border-line bg-surface-2">
              <th className="col-head px-4 py-2 text-left" style={{ fontSize: 10 }}>Category</th>
              <th className="col-head px-4 py-2 text-right" style={{ fontSize: 10, width: 160 }}>Cost (LUX)</th>
              <th className="col-head px-4 py-2 text-right" style={{ fontSize: 10, width: 140 }}>+ O&amp;P</th>
              <th className="col-head px-4 py-2 text-right" style={{ fontSize: 10, width: 160 }}>Client value</th>
            </tr>
          </thead>
          <tbody>
            <BreakdownRow label="Labour" icon={Hammer} cost={breakdown.labour_total} oapPct={a.oap_pct / 100}
              testId="breakdown-labour" />
            <BreakdownRow label="Materials" icon={Construction} cost={breakdown.materials_total} oapPct={a.oap_pct / 100}
              testId="breakdown-materials" />
            <BreakdownRow label="Plant" icon={Construction} cost={breakdown.plant_total} oapPct={a.oap_pct / 100}
              testId="breakdown-plant" />
            <BreakdownRow
              label="Pro Sums"
              hint="(includes subby allowances)"
              icon={Receipt}
              cost={breakdown.provsums_total}
              oapPct={a.oap_pct / 100}
              testId="breakdown-provsums"
            />
            <SubcontractorsRow
              total={breakdown.subcontractors_accepted_total}
              count={breakdown.subcontractors_accepted_count}
              quotes={breakdown.subcontractors_accepted}
              tenderId={tenderId}
            />
            <BreakdownRow label="Prelims" icon={Building2} cost={breakdown.prelims_total} oapPct={a.oap_pct / 100}
              testId="breakdown-prelims" />
            <BreakdownRow
              label="Contingency"
              hint="(LUX only)"
              icon={AlertTriangle}
              cost={breakdown.contingency_total}
              oapPct={a.oap_pct / 100}
              muted
              testId="breakdown-contingency"
            />
            <tr className="border-t-2 border-line bg-surface-2">
              <td className="px-4 py-2.5 text-ink" style={{ fontSize: 12, fontWeight: 600 }}>Subtotal</td>
              <td className="px-4 py-2.5 text-right text-ink tabular-nums" style={{ fontSize: 12.5, fontWeight: 600 }}>
                {gbp(breakdown.net_total)}
              </td>
              <td className="px-4 py-2.5 text-right text-ink tabular-nums" style={{ fontSize: 12.5, fontWeight: 600 }}>
                {gbp(breakdown.oap_total)}
              </td>
              <td className="px-4 py-2.5 text-right text-ink tabular-nums" style={{ fontSize: 13, fontWeight: 600 }}
                  data-testid="breakdown-subtotal">
                {gbp(breakdown.subtotal)}
              </td>
            </tr>
            {hasFinalAdj && (
              <tr className="border-t border-line/50">
                <td className="px-4 py-2.5" style={{ fontSize: 12.5, fontWeight: 500, color: "#A1311E" }}>
                  Final adjustment{a.final_adjustment_show_as_discount ? " (will show as discount on PDF)" : ""}
                </td>
                <td colSpan={2}></td>
                <td className="px-4 py-2.5 text-right tabular-nums" style={{ fontSize: 13, fontWeight: 600, color: "#A1311E" }}
                    data-testid="breakdown-final-adjustment">
                  {a.final_adjustment_value > 0 ? "+" : ""}{gbp(a.final_adjustment_value)}
                </td>
              </tr>
            )}
            <tr className="border-t-2 border-ink/15 bg-accent/5">
              <td className="px-4 py-3 text-ink" style={{ fontSize: 14, fontWeight: 700 }}>Client total</td>
              <td colSpan={2}></td>
              <td className="px-4 py-3 text-right text-ink tabular-nums" style={{ fontSize: 16, fontWeight: 700 }}
                  data-testid="breakdown-client-total">
                {gbp(breakdown.client_total)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* --- Hours per Trade (LT-HoursPerTrade-01) --- */}
      <HoursPerTradePanel tenderId={tenderId} />

      {/* --- (d) Final adjustment panel --- */}
      <FinalAdjustmentPanel
        open={adjOpen}
        onToggle={() => setAdjOpen((v) => !v)}
        value={a.final_adjustment_value}
        targetPrice={a.final_adjustment_target_price}
        notes={a.final_adjustment_notes}
        showAsDiscount={a.final_adjustment_show_as_discount}
        granularity={a.client_estimate_granularity}
        subtotal={breakdown.subtotal}
        clientTotal={breakdown.client_total}
        onApply={(payload) => setConfirmDialog({ kind: "apply", payload })}
        onRemove={() => setConfirmDialog({ kind: "remove" })}
        onSettingsChange={updateLocal}
      />

      {/* --- (e) Tender checks --- */}
      <TenderChecks checks={checks} clientTotal={breakdown.client_total}
        floorAreas={{
          ground: a.floor_area_ground_m2,
          first:  a.floor_area_first_m2,
          second: a.floor_area_second_m2,
        }}
        onFloorAreasChange={updateLocal}
      />

      {/* --- Confirm dialog --- */}
      <ApplyConfirmDialog
        confirm={confirmDialog}
        onClose={() => setConfirmDialog(null)}
        onApply={applyAdjustment}
        onRemove={removeAdjustment}
        subtotal={breakdown.subtotal}
      />
    </div>
  );
}


/* -------- Commercial-input pieces -------- */
function DurationInput({ calculated, adjustment, project, onChange }) {
  const [local, setLocal] = useState(adjustment ?? 0);
  useEffect(() => setLocal(adjustment ?? 0), [adjustment]);
  return (
    <div data-testid="adjudication-duration">
      <div className="col-head" style={{ fontSize: 10 }}>Project duration (wk)</div>
      <input
        type="number"
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => {
          const v = Number(local || 0);
          if (v !== Number(adjustment || 0)) onChange(v);
        }}
        step={0.5}
        className="w-full h-9 px-3 mt-1.5 rounded-md border border-line bg-surface text-ink tabular-nums focus:outline-none focus:ring-1 focus:ring-accent"
        style={{ fontSize: 13 }}
        data-testid="adjudication-duration-input"
      />
      <div className="text-ink-4 mt-1" style={{ fontSize: 11 }}>
        Calc: {num(calculated, 1)}wk · cascades to prelims
        {Number(adjustment) !== 0 && (
          <span className="ml-2 text-amber-700">manual adjust</span>
        )}
      </div>
      <div className="text-ink-3 mt-1" style={{ fontSize: 11 }}>
        Live total: <span className="text-ink tabular-nums">{num(project, 1)}wk</span>
      </div>
    </div>
  );
}

function PctInput({ label, value, onChange, testId, footer }) {
  const [local, setLocal] = useState(value ?? 0);
  useEffect(() => setLocal(value ?? 0), [value]);
  return (
    <div data-testid={testId}>
      <div className="col-head" style={{ fontSize: 10 }}>{label}</div>
      <div className="relative mt-1.5">
        <input
          type="number"
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          onBlur={() => {
            const v = Number(local || 0);
            if (v !== Number(value || 0)) onChange(v);
          }}
          step={0.1}
          className="w-full h-9 pl-3 pr-7 rounded-md border border-line bg-surface text-ink tabular-nums focus:outline-none focus:ring-1 focus:ring-accent"
          style={{ fontSize: 13 }}
          data-testid={`${testId}-input`}
        />
        <Percent size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-4" />
      </div>
      {footer && <div className="mt-1">{footer}</div>}
    </div>
  );
}

function ContingencyInput({ value, isPct, pct, computedTotal, onChange }) {
  const [localValue, setLocalValue] = useState(value ?? 0);
  const [localPct, setLocalPct] = useState(pct ?? 0);
  useEffect(() => setLocalValue(value ?? 0), [value]);
  useEffect(() => setLocalPct(pct ?? 0), [pct]);
  return (
    <div data-testid="adjudication-contingency">
      <div className="flex items-center justify-between">
        <span className="col-head" style={{ fontSize: 10 }}>Contingency</span>
        <button
          onClick={() => onChange({ contingency_is_percentage: !isPct })}
          className="text-accent hover:underline"
          style={{ fontSize: 10.5 }}
          data-testid="adjudication-contingency-toggle"
        >
          Switch to {isPct ? "£" : "%"}
        </button>
      </div>
      <div className="relative mt-1.5">
        <input
          type="number"
          value={isPct ? localPct : localValue}
          onChange={(e) => isPct ? setLocalPct(e.target.value) : setLocalValue(e.target.value)}
          onBlur={() => {
            if (isPct) {
              const v = Number(localPct || 0);
              if (v !== Number(pct || 0)) onChange({ contingency_pct: v });
            } else {
              const v = Number(localValue || 0);
              if (v !== Number(value || 0)) onChange({ contingency_value: v });
            }
          }}
          step={isPct ? 0.5 : 100}
          className="w-full h-9 pl-7 pr-3 rounded-md border border-line bg-surface text-ink tabular-nums focus:outline-none focus:ring-1 focus:ring-accent"
          style={{ fontSize: 13 }}
          data-testid="adjudication-contingency-input"
        />
        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-4" style={{ fontSize: 13 }}>
          {isPct ? "%" : "£"}
        </span>
      </div>
      <div className="text-ink-4 mt-1" style={{ fontSize: 11 }}>
        Computed: {gbp(computedTotal)} · LUX view only
      </div>
    </div>
  );
}

function BreakdownRow({ label, hint, icon: Icon, cost, oapPct, muted, testId }) {
  const oap = cost * oapPct;
  const client = cost + oap;
  return (
    <tr className={cls(
      "border-b border-line/60 last:border-0",
      muted && "bg-surface-2/40",
    )}>
      <td className="px-4 py-2 text-ink" style={{ fontSize: 12.5 }}>
        <div className="flex items-center gap-2">
          {Icon && <Icon size={13} className="text-ink-4" />}
          <span>{label}</span>
          {hint && <span className="text-ink-4" style={{ fontSize: 11 }}>{hint}</span>}
        </div>
      </td>
      <td className="px-4 py-2 text-right tabular-nums text-ink-2" data-testid={`${testId}-cost`}>
        {gbp(cost)}
      </td>
      <td className="px-4 py-2 text-right tabular-nums text-ink-3">{gbp(oap)}</td>
      <td className="px-4 py-2 text-right tabular-nums text-ink" data-testid={`${testId}-client`}>
        {gbp(client)}
      </td>
    </tr>
  );
}


/* LT-39 — accepted subby quotes. INFORMATIONAL: not part of cost basis or
   any total (Chris's call) — the row exists so the winning quotes are visible
   beside the cost breakdown instead of being buried in Pro Sums. */
function SubcontractorsRow({ total, count, quotes, tenderId }) {
  const [open, setOpen] = useState(false);
  const has = (count || 0) > 0;
  return (
    <>
      <tr className="border-b border-line/60 bg-surface-2/40" data-testid="breakdown-subcontractors">
        <td className="px-4 py-2 text-ink" style={{ fontSize: 12.5 }}>
          <div className="flex items-center gap-2 flex-wrap">
            <Hammer size={13} className="text-ink-4" />
            <span>Subcontractors</span>
            <span className="text-ink-4" style={{ fontSize: 11 }}>(accepted quotes · info only — not in totals)</span>
            {has ? (
              <button type="button" onClick={() => setOpen((v) => !v)} className="text-accent hover:underline" style={{ fontSize: 11 }} data-testid="breakdown-subcontractors-toggle">
                {open ? "Hide" : `${count} quote${count === 1 ? "" : "s"}`}
              </button>
            ) : (
              <a href={`/tenders/${tenderId}?tab=subby`} className="text-ink-4 hover:text-ink underline" style={{ fontSize: 11 }} data-testid="breakdown-subcontractors-none">none accepted yet</a>
            )}
          </div>
        </td>
        <td className="px-4 py-2 text-right tabular-nums text-ink-2" data-testid="breakdown-subcontractors-cost">{gbp(total || 0)}</td>
        <td className="px-4 py-2 text-right text-ink-4" style={{ fontSize: 11 }}>—</td>
        <td className="px-4 py-2 text-right text-ink-4" style={{ fontSize: 11 }}>—</td>
      </tr>
      {has && open && (quotes || []).map((q) => (
        <tr key={q.id} className="border-b border-line/40 bg-surface-2/20" data-testid={`breakdown-subcontractor-${q.id}`}>
          <td className="px-4 py-1.5 pl-10 text-ink-2" style={{ fontSize: 12 }}>
            {q.subcontractor_name}{q.trade_name ? <span className="text-ink-4"> · {q.trade_name}</span> : null}{q.reference ? <span className="text-ink-4"> · ref {q.reference}</span> : null}
          </td>
          <td className="px-4 py-1.5 text-right tabular-nums text-ink-2" style={{ fontSize: 12 }}>{gbp(q.amount)}</td>
          <td colSpan={2} />
        </tr>
      ))}
    </>
  );
}


/* -------- Final-adjustment panel -------- */
function FinalAdjustmentPanel({
  open, onToggle, value, targetPrice, notes, showAsDiscount, granularity,
  subtotal, clientTotal, onApply, onRemove, onSettingsChange,
}) {
  const isApplied = value !== 0;
  const [mode, setMode] = useState(targetPrice != null ? "target" : "manual");
  const [target, setTarget] = useState(targetPrice ?? clientTotal ?? "");
  const [manual, setManual] = useState(value ?? 0);
  const [reasonNotes, setReasonNotes] = useState(notes || "");
  useEffect(() => {
    setMode(targetPrice != null ? "target" : "manual");
    setTarget(targetPrice ?? clientTotal ?? "");
    setManual(value ?? 0);
    setReasonNotes(notes || "");
  }, [targetPrice, value, notes, clientTotal]);

  const calculated = useMemo(() => {
    if (mode === "target") {
      const t = Number(target || 0);
      return Math.round((t - subtotal) * 100) / 100;
    }
    return Math.round(Number(manual || 0) * 100) / 100;
  }, [mode, target, manual, subtotal]);

  const calcPctOfSubtotal = subtotal > 0 ? (calculated / subtotal) * 100 : 0;
  const reasonValid = (reasonNotes || "").trim().length > 0;

  return (
    <div className="rounded-lg border border-line bg-surface overflow-hidden" data-testid="adjudication-final-adjustment-panel">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-surface-2/50"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown size={14} className="text-ink-3" /> : <ChevronRight size={14} className="text-ink-3" />}
          <span className="text-ink" style={{ fontSize: 13.5, fontWeight: 600 }}>Final adjustment</span>
          <span
            className="inline-flex items-center px-1.5 py-0.5 rounded"
            style={{ fontSize: 10, background: "#F5F3EE", color: "#787068" }}
          >
            Optional · target price or manual £
          </span>
        </div>
        <div className="flex items-center gap-2">
          {isApplied ? (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded"
              style={{ fontSize: 11, background: "#FCE9E6", color: "#A1311E", fontWeight: 500 }}
              data-testid="adjudication-final-adjustment-status-active"
            >
              Active {value > 0 ? "+" : ""}{gbp(value)}
            </span>
          ) : (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded text-ink-4"
              style={{ fontSize: 11, background: "#F5F3EE" }}
              data-testid="adjudication-final-adjustment-status-inactive"
            >
              Not applied
            </span>
          )}
        </div>
      </button>
      {open && (
        <div className="p-4 border-t border-line space-y-4">
          {/* Mode toggle */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setMode("target")}
              className={cls(
                "h-8 px-3 rounded-md border",
                mode === "target"
                  ? "bg-accent text-white border-accent"
                  : "bg-surface text-ink-3 border-line hover:bg-surface-2",
              )}
              style={{ fontSize: 12 }}
              data-testid="adjudication-mode-target"
            >Target client price</button>
            <button
              onClick={() => setMode("manual")}
              className={cls(
                "h-8 px-3 rounded-md border",
                mode === "manual"
                  ? "bg-accent text-white border-accent"
                  : "bg-surface text-ink-3 border-line hover:bg-surface-2",
              )}
              style={{ fontSize: 12 }}
              data-testid="adjudication-mode-manual"
            >Manual adjustment (£)</button>
          </div>

          {/* Inputs */}
          <div className="grid grid-cols-2 gap-4">
            {mode === "target" ? (
              <div>
                <div className="col-head" style={{ fontSize: 10 }}>Target client price</div>
                <input
                  type="number"
                  value={target}
                  onChange={(e) => setTarget(e.target.value)}
                  step={100}
                  className="w-full h-9 px-3 mt-1.5 rounded-md border border-line bg-surface text-ink tabular-nums focus:outline-none focus:ring-1 focus:ring-accent"
                  style={{ fontSize: 13 }}
                  data-testid="adjudication-target-price-input"
                />
                <div className="text-ink-4 mt-1" style={{ fontSize: 11 }}>
                  Subtotal before adjustment: {gbp(subtotal)}
                </div>
              </div>
            ) : (
              <div>
                <div className="col-head" style={{ fontSize: 10 }}>Manual adjustment</div>
                <input
                  type="number"
                  value={manual}
                  onChange={(e) => setManual(e.target.value)}
                  step={100}
                  className="w-full h-9 px-3 mt-1.5 rounded-md border border-line bg-surface text-ink tabular-nums focus:outline-none focus:ring-1 focus:ring-accent"
                  style={{ fontSize: 13 }}
                  placeholder="-500 for a discount, 1000 for an uplift"
                  data-testid="adjudication-manual-value-input"
                />
              </div>
            )}
            <div>
              <div className="col-head" style={{ fontSize: 10 }}>Calculated adjustment</div>
              <div
                className="h-9 px-3 mt-1.5 rounded-md flex items-center tabular-nums"
                style={{ fontSize: 13, color: "#A1311E", background: "#FCE9E6" }}
                data-testid="adjudication-calculated-adjustment"
              >
                {calculated > 0 ? "+" : ""}{gbp(calculated)}
                <span className="text-ink-4 ml-2" style={{ fontSize: 11 }}>
                  ({calcPctOfSubtotal > 0 ? "+" : ""}{num(calcPctOfSubtotal, 1)}% of subtotal)
                </span>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <div className="col-head" style={{ fontSize: 10 }}>
              Reason for adjustment <span style={{ color: "#A1311E" }}>*</span>
            </div>
            <textarea
              value={reasonNotes}
              onChange={(e) => setReasonNotes(e.target.value)}
              rows={3}
              className="w-full mt-1.5 px-3 py-2 rounded-md border border-line bg-surface text-ink focus:outline-none focus:ring-1 focus:ring-accent"
              style={{ fontSize: 12.5 }}
              placeholder="e.g. Closing the gap to a competitor's price; client requested round number; …"
              data-testid="adjudication-final-adjustment-notes"
            />
            {!reasonValid && (
              <div className="text-ink-4 mt-1" style={{ fontSize: 11 }}>
                Notes are required before the adjustment can be applied.
              </div>
            )}
          </div>

          {/* Toggles */}
          <div className="flex items-center gap-6 pt-1">
            <label className="inline-flex items-center gap-2 text-ink-2" style={{ fontSize: 12 }}>
              <input
                type="checkbox"
                checked={!!showAsDiscount}
                onChange={(e) => onSettingsChange({ final_adjustment_show_as_discount: e.target.checked })}
                data-testid="adjudication-show-as-discount"
              />
              Show as discount on PDF
              <span className="text-ink-4 ml-1" style={{ fontSize: 11 }}>(presentation only)</span>
            </label>
            <div className="flex items-center gap-2">
              <span className="text-ink-3" style={{ fontSize: 12 }}>Apply method:</span>
              <span className="text-ink-2" style={{ fontSize: 12, fontWeight: 500 }}>
                Spread evenly across line items
              </span>
              <span className="text-ink-4" style={{ fontSize: 11 }}>(LT-12 PDF will pro-rata)</span>
            </div>
          </div>

          {/* Granularity */}
          <div>
            <div className="col-head" style={{ fontSize: 10 }}>Client estimate granularity (LT-12)</div>
            <div className="flex items-center gap-2 mt-1.5">
              <button
                onClick={() => onSettingsChange({ client_estimate_granularity: "line_item" })}
                className={cls(
                  "h-7 px-3 rounded-md border",
                  granularity === "line_item"
                    ? "bg-accent text-white border-accent"
                    : "bg-surface text-ink-3 border-line hover:bg-surface-2",
                )}
                style={{ fontSize: 11.5 }}
                data-testid="adjudication-granularity-line-item"
              >Line item</button>
              <button
                onClick={() => onSettingsChange({ client_estimate_granularity: "phase_only" })}
                className={cls(
                  "h-7 px-3 rounded-md border",
                  granularity === "phase_only"
                    ? "bg-accent text-white border-accent"
                    : "bg-surface text-ink-3 border-line hover:bg-surface-2",
                )}
                style={{ fontSize: 11.5 }}
                data-testid="adjudication-granularity-phase-only"
              >Phase-only</button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-2 border-t border-line">
            <div>
              {isApplied && (
                <button
                  onClick={() => onRemove()}
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-line text-note-red hover:bg-surface-2"
                  style={{ fontSize: 12 }}
                  data-testid="adjudication-remove-adjustment-btn"
                >
                  <X size={13} /> Remove adjustment
                </button>
              )}
            </div>
            <button
              onClick={() => onApply({
                target_client_price: mode === "target" ? Number(target || 0) : null,
                manual_value:        mode === "manual" ? Number(manual || 0) : null,
                notes:               reasonNotes,
                calculated,
              })}
              disabled={!reasonValid || calculated === 0}
              className={cls(
                "inline-flex items-center gap-1.5 h-8 px-3.5 rounded-md text-white shadow-soft",
                (!reasonValid || calculated === 0)
                  ? "bg-ink-4 cursor-not-allowed"
                  : "bg-accent hover:bg-accent-deep",
              )}
              style={{ fontSize: 12.5, fontWeight: 500 }}
              data-testid="adjudication-apply-adjustment-btn"
            >
              <Save size={13} /> Apply adjustment
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


/* -------- Tender-checks panel -------- */
function TenderChecks({ checks, clientTotal, floorAreas, onFloorAreasChange }) {
  if (!checks) return null;
  const tone = TRAFFIC_LIGHT[checks.prelims_traffic_light] || TRAFFIC_LIGHT.amber;
  return (
    <div className="rounded-lg border border-line bg-surface p-4 space-y-4" data-testid="adjudication-tender-checks">
      <h3 className="text-ink" style={{ fontSize: 14, fontWeight: 600 }}>
        Tender checks <span className="text-ink-4 font-normal" style={{ fontSize: 11 }}>— sanity ratios, read-only</span>
      </h3>

      <div className="grid grid-cols-3 gap-3">
        <CheckCard
          label="Prelims % of net"
          value={`${num(checks.prelims_pct_of_net, 1)}%`}
          tone={tone}
          testId="check-prelims-pct"
        />
        <CheckCard
          label="Effective margin %"
          value={`${num(checks.effective_margin_pct, 1)}%`}
          testId="check-effective-margin"
        />
        <CheckCard
          label="Total man hours"
          value={num(checks.total_man_hours, 0)}
          testId="check-total-hours"
        />
      </div>

      {/* Square meter check */}
      <div className="border-t border-line pt-4">
        <div className="col-head mb-2" style={{ fontSize: 10 }}>Square meter check</div>
        <div className="grid grid-cols-3 gap-3 mb-3">
          <FloorAreaInput label="Ground floor m²" value={floorAreas.ground} onChange={(v) => onFloorAreasChange({ floor_area_ground_m2: v })} testId="floor-ground" />
          <FloorAreaInput label="First floor m²" value={floorAreas.first} onChange={(v) => onFloorAreasChange({ floor_area_first_m2: v })} testId="floor-first" />
          <FloorAreaInput label="Second floor m²" value={floorAreas.second} onChange={(v) => onFloorAreasChange({ floor_area_second_m2: v })} testId="floor-second" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <BenchmarkCard label="@£2k/m²"  value={checks.benchmarks.benchmark_2k_total} testId="benchmark-2k" />
          <BenchmarkCard label="@£2.5k/m²" value={checks.benchmarks.benchmark_2_5k_total} testId="benchmark-2_5k" />
          <BenchmarkCard label="@£3k/m²"  value={checks.benchmarks.benchmark_3k_total} testId="benchmark-3k" />
        </div>
        <div className="text-ink-3 mt-3" style={{ fontSize: 12 }} data-testid="benchmark-commentary">
          {checks.benchmarks.commentary}
        </div>
      </div>

      {/* Day-rate check */}
      <div className="border-t border-line pt-4">
        <div className="col-head mb-2" style={{ fontSize: 10 }}>Day rate check</div>
        <div className="grid grid-cols-2 gap-3">
          <CheckCard
            label={`Expected (£${num(checks.checks_config.hourly_rate, 0)} × ${num(checks.checks_config.hours_per_day, 0)}h × ${num(checks.checks_config.avg_men_per_day, 1)})`}
            value={gbp(checks.expected_day_rate)}
            sub="per day"
            testId="day-rate-expected"
          />
          <CheckCard
            label="Avg labour/day from estimate"
            value={gbp(checks.avg_labour_per_day)}
            sub="actual"
            testId="day-rate-actual"
          />
        </div>
        <div className="text-ink-4 mt-2" style={{ fontSize: 11 }}>
          Constants editable in <a href="/settings?tab=benchmarks" className="text-accent hover:underline">Settings → Benchmarks &amp; Defaults</a>.
        </div>
      </div>
    </div>
  );
}

function CheckCard({ label, value, sub, tone, testId }) {
  return (
    <div
      className="rounded-md border px-3 py-2.5"
      style={{
        background: tone?.bg || "#F8F6F2",
        borderColor: tone?.border || "var(--color-line)",
      }}
      data-testid={testId}
    >
      <div className="col-head" style={{ fontSize: 10, color: tone?.text }}>{label}</div>
      <div className="text-ink mt-0.5 tabular-nums" style={{ fontSize: 16, fontWeight: 600, color: tone?.text }}>
        {value}
      </div>
      {sub && <div className="text-ink-4 mt-0.5" style={{ fontSize: 10.5 }}>{sub}</div>}
    </div>
  );
}

function FloorAreaInput({ label, value, onChange, testId }) {
  const [local, setLocal] = useState(value ?? 0);
  useEffect(() => setLocal(value ?? 0), [value]);
  return (
    <div data-testid={testId}>
      <div className="col-head" style={{ fontSize: 10 }}>{label}</div>
      <input
        type="number"
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => {
          const v = Number(local || 0);
          if (v !== Number(value || 0)) onChange(v);
        }}
        step={1}
        className="w-full h-8 px-2.5 mt-1.5 rounded-md border border-line bg-surface text-ink tabular-nums focus:outline-none focus:ring-1 focus:ring-accent"
        style={{ fontSize: 12.5 }}
      />
    </div>
  );
}

function BenchmarkCard({ label, value, testId }) {
  return (
    <div
      className="rounded-md border border-line bg-surface-2/40 px-3 py-2.5"
      data-testid={testId}
    >
      <div className="col-head" style={{ fontSize: 10 }}>{label}</div>
      <div className="text-ink mt-0.5 tabular-nums" style={{ fontSize: 14, fontWeight: 600 }}>
        {gbp(value)}
      </div>
    </div>
  );
}


/* -------- Confirm dialog -------- */
function ApplyConfirmDialog({ confirm, onClose, onApply, onRemove, subtotal }) {
  if (!confirm) return null;
  if (confirm.kind === "apply") {
    const calc = confirm.payload.calculated ?? 0;
    return (
      <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-md" data-testid="adjudication-apply-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Apply this adjustment?</DialogTitle>
          </DialogHeader>
          <div className="space-y-2" style={{ fontSize: 13 }}>
            <p>This will change the client total by <span className="text-ink tabular-nums" style={{ fontWeight: 600 }}>{calc > 0 ? "+" : ""}{gbp(calc)}</span>.</p>
            <p className="text-ink-3" style={{ fontSize: 12 }}>
              Reason: <span className="italic">{confirm.payload.notes}</span>
            </p>
          </div>
          <DialogFooter>
            <button
              onClick={onClose}
              className="h-8 px-3 rounded-md border border-line text-ink-2 hover:bg-surface-2"
              style={{ fontSize: 12 }}
            >Cancel</button>
            <button
              onClick={() => onApply(confirm.payload)}
              className="h-8 px-3.5 rounded-md bg-accent text-white hover:bg-accent-deep"
              style={{ fontSize: 12, fontWeight: 500 }}
              data-testid="adjudication-apply-confirm-yes"
            >Apply</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="adjudication-remove-confirm-dialog">
        <DialogHeader>
          <DialogTitle>Remove final adjustment?</DialogTitle>
        </DialogHeader>
        <p style={{ fontSize: 13 }}>The client total will revert to {gbp(subtotal)}.</p>
        <DialogFooter>
          <button
            onClick={onClose}
            className="h-8 px-3 rounded-md border border-line text-ink-2 hover:bg-surface-2"
            style={{ fontSize: 12 }}
          >Cancel</button>
          <button
            onClick={onRemove}
            className="h-8 px-3.5 rounded-md text-white shadow-soft"
            style={{ fontSize: 12, fontWeight: 500, background: "#A1311E" }}
            data-testid="adjudication-remove-confirm-yes"
          >Remove</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
